import { DtoBase } from "../../DtoBase";
import { HashSet, Dictionary, Iterable } from "DiscoveryEngine.TS/Utils";
import { ObjSerUtils } from "DiscoveryEngine.TS/Serialization";
import { DirectoryGroupDto, DirectoryUsersDto, DirectoryUserDto, AccountType } from "UvExplorerServer.Dtos.TS/Auth";


export class DirectoryGroupsDto extends DtoBase {

	private static readonly Default_AccountType = AccountType.Native;

	public AccountType: AccountType;
	public DomainName: string;
	public Groups: HashSet<DirectoryGroupDto>;
	// Not serialized
	private GroupIdIndex: Dictionary<string, DirectoryGroupDto>;

	constructor() {
		super();

		this.AccountType = DirectoryGroupsDto.Default_AccountType;
		this.DomainName = "";
		this.Groups = new HashSet<DirectoryGroupDto>();
		this.GroupIdIndex = new Dictionary<string, DirectoryGroupDto>();
	}

	public Clear(): void {
		this.AccountType = DirectoryGroupsDto.Default_AccountType;
		this.DomainName = "";
		this.Groups.Clear();
		this.GroupIdIndex.Clear();
	}

	public get HasDomainName(): boolean {
		return !!this.DomainName;
	}

	public get HasGroups(): boolean {
		return (!!this.Groups && this.Groups.Count > 0);
	}

	public Add(group: DirectoryGroupDto): void {

		console.assert(!this.Groups.Contains(group));
		console.assert(!group.HasEphemeralId || !this.GroupIdIndex.ContainsKey(group.EphemeralId));
		console.assert(!group.HasPermanentId || !this.GroupIdIndex.ContainsKey(group.PermanentId));

		this.Groups.Add(group);

		if (group.HasEphemeralId)
			this.GroupIdIndex.Add(group.EphemeralId, group);

		if (group.HasPermanentId)
			this.GroupIdIndex.Add(group.PermanentId, group);
	}

	public AddRange(groups: Iterable<DirectoryGroupDto>): void {
		groups.ForEach((g: DirectoryGroupDto) => {
			this.Add(g);
		});
	}

	public Remove(group: DirectoryGroupDto): boolean {
		if (this.Groups.Contains(group)) {

			this.Groups.Remove(group);

			if (group.HasEphemeralId) {
				this.GroupIdIndex.Remove(group.EphemeralId);
			}

			if (group.HasPermanentId) {
				this.GroupIdIndex.Remove(group.PermanentId);
			}

			return true;
		}
		else {
			return false;
		}
	}

	public FindGroupById(id: string): DirectoryGroupDto {

		if (this.GroupIdIndex.ContainsKey(id)) {
			return this.GroupIdIndex.Item(id);
		}

		return null;
	}

	public get TopLevelGroups(): HashSet<DirectoryGroupDto> {
		let result = new HashSet<DirectoryGroupDto>();

		this.Groups.ForEach((group: DirectoryGroupDto) => {
			if (!group.HasParentGroups) {
				result.Add(group);
			}
		});

		return result;
	}

	public ResolveReferences(users: DirectoryUsersDto): boolean {

		let missingGroups = false;

		this.Groups.ForEach((group: DirectoryGroupDto) => {
			group.ParentGroups.Clear();
			group.MemberGroups.Clear();
			group.MemberUsers.Clear();

			group.MemberGroupIds.ForEach((memberGroupId: string) => {
				let memberGroup: DirectoryGroupDto = this.FindGroupById(memberGroupId);
				if (memberGroup) {
					group.MemberGroups.Add(memberGroup);
					memberGroup.ParentGroups.Add(group);
				}
				else {
					missingGroups = true;
				}
			});

			if (users) {
				group.MemberUserIds.ForEach((memberUserId: string) => {
					let memberUser: DirectoryUserDto = users.FindUserById(memberUserId);
					if (memberUser) {
						group.MemberUsers.Add(memberUser);
						memberUser.ParentGroups.Add(group);
					}
				});
			}
		});

		return !missingGroups;
	}

	public Write(obj: any, context: any): void {
		obj.directory_groups = {};
		obj = obj.directory_groups;

		super.Write(obj, context);

		if (this.AccountType !== DirectoryGroupsDto.Default_AccountType) {
			obj.account_type = AccountType[this.AccountType];
		}

		if (this.HasDomainName) {
			obj.domain_name = this.DomainName;
		}

		if (this.HasGroups) {
			obj.groups = [];
			ObjSerUtils.WriteObjSerCollection(obj.groups, this.Groups, {});
		}
	}

	public Read(obj: any, context: any): void {
		if (!obj.directory_groups)
			throw new Error();
		obj = obj.directory_groups;

		super.Read(obj, context);

		if (obj.account_type !== undefined) {
			this.AccountType = AccountType[<string>obj.account_type];
		}

		if (obj.domain_name !== undefined) {
			this.DomainName = obj.domain_name;
		}

		if (obj.groups !== undefined) {
			this.Clear();
			obj.groups.forEach((elem: any) => {
				let group = new DirectoryGroupDto();
				group.Read(elem, context);
				this.Add(group);
			}, this);
		}
	}

}